import React, { ReactNode, useEffect, useState } from 'react';
import styles from './Tabs.module.scss';
import Button, { ButtonType } from './../button/Button';
import { hasPermission } from '../../../../guards/common/authentification-functions';

export interface TabItem {
  key: string;
  label: ReactNode;
  content: ReactNode;
  actionButton?: {
    text: string;
    onClick: () => void;
    type?: ButtonType;
    permission?: number;
  };
}

interface TabsProps {
  tabs: TabItem[];
  defaultActiveKey?: string;
  activeTab?: string;
  hideBorder?: boolean;
  onTabChange?: (key: string) => void;
  /** Content for the right of the tab bar, for cases an actionButton cannot cover. */
  headerRightContent?: ReactNode;
}

const Tabs: React.FC<TabsProps> = ({
  tabs,
  defaultActiveKey,
  activeTab,
  hideBorder,
  onTabChange,
  headerRightContent,
}) => {
  const [internalActiveKey, setInternalActiveKey] = useState(defaultActiveKey ?? tabs[0]?.key);
  
  const isControlled = activeTab !== undefined;
  
  const currentActiveKey = isControlled ? activeTab : internalActiveKey;

  useEffect(() => {
    if (activeTab !== undefined) {
      setInternalActiveKey(activeTab);
    }
  }, [activeTab]);

  const handleTabClick = (key: string) => {
    setInternalActiveKey(key);
    
    if (onTabChange) {
      onTabChange(key);
    }
  };

  const activeTabItem = tabs.find(tab => tab.key === currentActiveKey);
  const activeTabButton = activeTabItem?.actionButton;

  return (
    <div className={styles.tabsContainer}>
      <div className={`${styles.tabsHeader} ${hideBorder ? styles.hiddenBorder : ''}`}>
        <div className={styles.tabsList}>
          {tabs.map(tab => (
            <div
              key={tab.key}
              className={`${styles.tabItem} ${currentActiveKey === tab.key ? styles.active : ''}`}
              onClick={() => handleTabClick(tab.key)}
            >
              {tab.label}
            </div>
          ))}
        </div>
        
        {activeTabButton && (!activeTabButton.permission || hasPermission(activeTabButton.permission)) && (
          <div className={styles.actionButton}>
            <Button
              text={activeTabButton.text}
              variant={activeTabButton.type ?? 'primary'}
              onClick={activeTabButton.onClick}
            />
          </div>
        )}

        {headerRightContent}
      </div>
      
      <div className={styles.tabContent}>
        {activeTabItem?.content}
      </div>
    </div>
  );
};

export default Tabs;